import React, { useState, useEffect, useRef } from 'react'
import { getImageUrl } from '../../utils/imageUtils'

interface ImageCarouselProps {
  images: string[]
  title?: string
  showThumbnails?: boolean
  showAutoRotate?: boolean
  autoRotateInterval?: number // en ms, default 5000
  onImageChange?: (index: number) => void
  className?: string
  imageContainerClassName?: string
  primaryColor?: string
  thumbnailSize?: 'sm' | 'md' | 'lg' // pequeño, mediano, grande
  useLazyLoading?: boolean // Lazy-load de imágenes (default: true)
  enableKeyboardShortcuts?: boolean // Habilitar atajos teclado globales (default: true)
}

const DEFAULT_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xNzUgMTIwSDE3MEMxNjcuMjM5IDEyMCAxNjUgMTIyLjIzOSAxNjUgMTI1VjE3NUMxNjUgMTc3Ljc2MSAxNjcuMjM5IDE4MCAxNzAgMTgwSDE3NUMxNzcuNzYxIDE4MCAxODAgMTc3Ljc2MSAxODAgMTc1VjEyNUMxODAgMTIyLjIzOSAxNzcuNzYxIDEyMCAxNzUgMTIwWiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMjAwIDEyMEgxOTVDMTkyLjIzOSAxMjAgMTkwIDEyMi4yMzkgMTkwIDEyNVYxNzVDMTkwIDE3Ny43NjEgMTkyLjIzOSAxODAgMTk1IDE4MEgyMDBDMjAyLjc2MSAxODAgMjA1IDE3Ny43NjEgMjA1IDE3NVYxMjVDMjA1IDEyMi4yMzkgMjAyLjc2MSAxMjAgMjAwIDEyMFoiIGZpbGw9IiM5Q0EzQUYiLz4KPHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeD0iMTgwIiB5PSIxMzAiPgo8Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIyMCIgZmlsbD0iIzZCNzI4MCIvPgo8cGF0aCBkPSJNMTYuNjY2NyAyMy4zMzMzTDIwIDIwTDIzLjMzMzMgMjMuMzMzM00yMCAxNi42NjY3VjIzLjMzMzMiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+Cjwvc3ZnPgo8dGV4dCB4PSIyMDAiIHk9IjIwMCIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM2QjcyODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkVzcGFjaW8gZGUgQ2FtcGluZzwvdGV4dD4KPC9zdmc+'

const getThumbnailSize = (size: 'sm' | 'md' | 'lg') => {
  switch (size) {
    case 'sm':
      return 'w-12 h-12'
    case 'lg':
      return 'w-20 h-20'
    case 'md':
    default:
      return 'w-16 h-16'
  }
}

// Componente helper para lazy-load con soporte para error
interface LazyImageProps {
  src: string
  alt: string
  className?: string
  title?: string
  shouldLoad: boolean
}

const LazyImage: React.FC<LazyImageProps> = ({ src, alt, className = '', title, shouldLoad }) => {
  const [imageSrc, setImageSrc] = useState<string>(shouldLoad ? getImageUrl(src) : '')
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!shouldLoad) {
      setImageSrc('')
      return
    }
    setImageSrc(getImageUrl(src))
  }, [src, shouldLoad])

  const handleError = () => {
    setError(true)
    setImageSrc(DEFAULT_IMAGE)
  }

  return (
    <img
      src={imageSrc || DEFAULT_IMAGE}
      alt={alt}
      className={className}
      title={title}
      onError={handleError}
      loading="lazy"
    />
  )
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images,
  title,
  showThumbnails = true,
  showAutoRotate = false,
  autoRotateInterval = 5000,
  onImageChange,
  className = '',
  imageContainerClassName = 'h-48 sm:h-64 md:h-80 lg:h-96',
  primaryColor = '#060A91',
  thumbnailSize = 'md',
  useLazyLoading = true,
  enableKeyboardShortcuts = true,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isZoomOpen, setIsZoomOpen] = useState(false)
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([0])) // Lazy loading tracker
  const carouselRef = useRef<HTMLDivElement>(null)

  // Normalizar imágenes: usar array vacío si no hay imágenes
  const imageList = images && images.length > 0 ? images : [DEFAULT_IMAGE]

  // Navegación con teclado global (cuando está enfocado)
  useEffect(() => {
    if (!enableKeyboardShortcuts || !carouselRef.current) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Solo si el carrusel está enfocado o el zoom está abierto
      const isCarouselFocused = carouselRef.current?.contains(document.activeElement)
      if (!isZoomOpen && !isCarouselFocused) return

      switch (e.key) {
        case 'Escape':
          if (isZoomOpen) {
            e.preventDefault()
            setIsZoomOpen(false)
          }
          break
        case 'ArrowLeft':
        case 'a':
        case 'A':
          e.preventDefault()
          setCurrentImageIndex((prev) =>
            prev === 0 ? imageList.length - 1 : prev - 1
          )
          break
        case 'ArrowRight':
        case 'd':
        case 'D':
          e.preventDefault()
          setCurrentImageIndex((prev) =>
            prev === imageList.length - 1 ? 0 : prev + 1
          )
          break
        case ' ':
          // Spacebar: siguiente imagen
          e.preventDefault()
          setCurrentImageIndex((prev) =>
            prev === imageList.length - 1 ? 0 : prev + 1
          )
          break
        case 'Home':
          e.preventDefault()
          setCurrentImageIndex(0)
          break
        case 'End':
          e.preventDefault()
          setCurrentImageIndex(imageList.length - 1)
          break
        default:
          // Números 1-9 para saltar a imagen específica
          if (e.key >= '1' && e.key <= '9') {
            const num = parseInt(e.key) - 1
            if (num < imageList.length) {
              e.preventDefault()
              setCurrentImageIndex(num)
            }
          }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isZoomOpen, imageList.length, enableKeyboardShortcuts])

  // Lazy loading: cargar imágenes adyacentes cuando cambia el índice
  useEffect(() => {
    if (!useLazyLoading) return

    setLoadedImages((prev) => {
      const indicesToLoad = new Set(prev)
      // Cargar imagen actual
      indicesToLoad.add(currentImageIndex)
      // Cargar siguiente (preload)
      const nextIndex = (currentImageIndex + 1) % imageList.length
      indicesToLoad.add(nextIndex)
      // Cargar anterior (preload)
      const prevIndex = currentImageIndex === 0 ? imageList.length - 1 : currentImageIndex - 1
      indicesToLoad.add(prevIndex)

      return indicesToLoad
    })
  }, [currentImageIndex, imageList.length, useLazyLoading])

  // Auto-rotación (solo si está habilitado)
  useEffect(() => {
    if (!showAutoRotate || imageList.length <= 1) return

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % imageList.length)
    }, autoRotateInterval)

    return () => clearInterval(interval)
  }, [showAutoRotate, autoRotateInterval, imageList.length])

  // Callback cuando cambia la imagen
  useEffect(() => {
    onImageChange?.(currentImageIndex)
  }, [currentImageIndex, onImageChange])

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? imageList.length - 1 : prev - 1
    )
  }

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === imageList.length - 1 ? 0 : prev + 1
    )
  }

  const currentImage = imageList[currentImageIndex]

  return (
    <div className={`space-y-2 ${className}`} ref={carouselRef} role="region" aria-label="Carrusel de imágenes">
      {/* Carrusel Principal */}
      <div
        className={`relative bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center cursor-pointer hover:shadow-lg transition-shadow ${imageContainerClassName}`}
        onClick={() => setIsZoomOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setIsZoomOpen(true)
          }
        }}
        title={enableKeyboardShortcuts ? '↑: abrir zoom | ← →: anterior/siguiente | ESPACIO: siguiente | 1-9: saltar | ESC: cerrar' : 'Click para zoom'}
      >
        <LazyImage
          src={currentImage}
          alt={title ? `${title} - Foto ${currentImageIndex + 1}` : `Foto ${currentImageIndex + 1}`}
          className="w-full h-full object-contain"
          shouldLoad={useLazyLoading ? loadedImages.has(currentImageIndex) : true}
        />

        {/* Botones de navegación */}
        {imageList.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation()
                handlePrevImage()
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors"
              title="Anterior (← arrow)"
              aria-label="Imagen anterior"
            >
              ❮
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleNextImage()
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors"
              title="Siguiente (→ arrow)"
              aria-label="Imagen siguiente"
            >
              ❯
            </button>

            {/* Indicador de posición */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm font-medium">
              {currentImageIndex + 1} / {imageList.length}
            </div>
          </>
        )}
      </div>

      {/* Miniaturas */}
      {showThumbnails && imageList.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {imageList.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentImageIndex(idx)}
              className={`flex-shrink-0 ${getThumbnailSize(thumbnailSize)} rounded-lg border-2 overflow-hidden transition-all ${
                idx === currentImageIndex
                  ? 'border-opacity-100'
                  : 'border-gray-200 hover:border-gray-400'
              }`}
              style={
                idx === currentImageIndex
                  ? {
                      borderColor: primaryColor,
                      boxShadow: `0 0 0 2px ${primaryColor}40`,
                    }
                  : {}
              }
              title={`Ir a imagen ${idx + 1}`}
              aria-label={`Imagen ${idx + 1} de ${imageList.length}`}
            >
              <LazyImage
                src={img}
                alt={`Miniatura ${idx + 1}`}
                className="w-full h-full object-contain"
                shouldLoad={useLazyLoading ? loadedImages.has(idx) : true}
              />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox / Zoom Modal */}
      {isZoomOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4"
          onClick={() => setIsZoomOpen(false)}
          role="dialog"
          aria-label="Visor de imagen a pantalla completa"
        >
          <div
            className="relative max-w-5xl max-h-[95vh] w-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Imagen a tamaño completo */}
            <div className="relative w-full h-full flex items-center justify-center">
              <LazyImage
                src={currentImage}
                alt={title ? `${title} - Foto ${currentImageIndex + 1} (Zoom)` : `Foto ${currentImageIndex + 1} (Zoom)`}
                className="max-w-full max-h-[85vh] object-contain"
                shouldLoad={isZoomOpen}
              />

              {/* Botón cerrar */}
              <button
                onClick={() => setIsZoomOpen(false)}
                className="absolute top-4 right-4 bg-white/20 hover:bg-white/40 text-white rounded-full w-12 h-12 flex items-center justify-center transition-colors text-2xl"
                title="Cerrar (ESC)"
                aria-label="Cerrar visor"
              >
                ✕
              </button>

              {/* Navegación en zoom */}
              {imageList.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handlePrevImage()
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white rounded-full w-14 h-14 flex items-center justify-center transition-colors text-2xl"
                    title="Anterior (← arrow)"
                    aria-label="Imagen anterior (zoom)"
                  >
                    ❮
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleNextImage()
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white rounded-full w-14 h-14 flex items-center justify-center transition-colors text-2xl"
                    title="Siguiente (→ arrow)"
                    aria-label="Imagen siguiente (zoom)"
                  >
                    ❯
                  </button>

                  {/* Indicador de posición en zoom */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full text-base font-semibold">
                    {currentImageIndex + 1} / {imageList.length}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ImageCarousel
